import { INTAKE_EXPIRY_GRACE_MS, INTAKE_POLL_INTERVAL_MS } from '../intake-client';

/**
 * Polls the intake status every few seconds while the page is visible
 * (spec §7.1): paused while hidden, checked immediately when the page shows
 * again, never two requests at once, gentle backoff after failures. With
 * `expiry`, it also stops by itself once the address's local deadline plus a
 * grace period has passed, even while the server cannot be reached.
 */

export { INTAKE_POLL_INTERVAL_MS };
const MAX_BACKOFF_FACTOR = 4;
/** How often the local deadline is checked, also while hidden or unreachable. */
export const EXPIRY_CHECK_INTERVAL_MS = 15_000;

export interface PollerVisibility {
  isVisible(): boolean;
  /** Returns an unsubscribe function. */
  subscribe(listener: () => void): () => void;
}

export interface IntakePollerExpiry {
  /** The address's deadline on this device's clock; read again on every check. */
  readonly deadline: () => number;
  /** Called once when the poller stopped itself because deadline + grace passed. */
  readonly onExpired: () => void;
  /** Defaults to INTAKE_EXPIRY_GRACE_MS. */
  readonly graceMs?: number;
}

export interface IntakePollerOptions<T> {
  readonly fetchStatus: (signal: AbortSignal) => Promise<T>;
  readonly onStatus: (status: T) => void;
  readonly onError: (error: unknown, consecutiveFailures: number) => void;
  readonly visibility: PollerVisibility;
  readonly intervalMs?: number;
  readonly expiry?: IntakePollerExpiry;
  /** Clock for the expiry check. Defaults to Date.now. */
  readonly now?: () => number;
}

export interface IntakePoller {
  start(): void;
  stop(): void;
  /** Check right away (no-op while a request is in flight or when stopped). */
  pollNow(): void;
  readonly running: boolean;
}

export function documentVisibility(target: Document = document): PollerVisibility {
  return {
    isVisible: () => target.visibilityState !== 'hidden',
    subscribe(listener) {
      target.addEventListener('visibilitychange', listener);
      return () => target.removeEventListener('visibilitychange', listener);
    },
  };
}

export function createIntakePoller<T>(options: IntakePollerOptions<T>): IntakePoller {
  const interval = options.intervalMs ?? INTAKE_POLL_INTERVAL_MS;
  const now = options.now ?? Date.now;
  const { expiry } = options;
  const graceMs = expiry?.graceMs ?? INTAKE_EXPIRY_GRACE_MS;
  let running = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let watchdog: ReturnType<typeof setInterval> | null = null;
  let controller: AbortController | null = null;
  let failures = 0;
  let unsubscribe: (() => void) | null = null;

  const clearTimer = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };

  const schedule = () => {
    clearTimer();
    if (!running || controller || !options.visibility.isVisible()) return;
    const factor = Math.min(MAX_BACKOFF_FACTOR, 2 ** Math.max(0, failures - 1));
    timer = setTimeout(pollNow, failures ? interval * factor : interval);
  };

  function stop(): void {
    running = false;
    clearTimer();
    if (watchdog !== null) clearInterval(watchdog);
    watchdog = null;
    const current = controller;
    controller = null;
    current?.abort();
    unsubscribe?.();
    unsubscribe = null;
  }

  /** Stops and reports once the local deadline plus grace has passed. No server answer needed. */
  function expireIfPast(): boolean {
    if (!running || !expiry || !(now() > expiry.deadline() + graceMs)) return false;
    stop();
    expiry.onExpired();
    return true;
  }

  function pollNow(): void {
    if (!running || controller || expireIfPast()) return;
    clearTimer();
    const current = new AbortController();
    controller = current;
    options.fetchStatus(current.signal).then(
      (status) => {
        if (controller !== current) return;
        failures = 0;
        controller = null;
        // The server answered, so it decides; onStatus refreshes the deadline.
        options.onStatus(status);
        schedule();
      },
      (error: unknown) => {
        if (controller !== current) return;
        controller = null;
        if (current.signal.aborted) return;
        failures += 1;
        if (expireIfPast()) return;
        options.onError(error, failures);
        schedule();
      },
    );
  }

  const onVisibility = () => {
    if (!running) return;
    if (options.visibility.isVisible()) pollNow();
    else clearTimer();
  };

  return {
    start() {
      if (running) return;
      running = true;
      failures = 0;
      if (expireIfPast()) return;
      unsubscribe = options.visibility.subscribe(onVisibility);
      // Timers keep running while the page is hidden, so the address expires
      // on time even when no request is made.
      if (expiry) watchdog = setInterval(expireIfPast, EXPIRY_CHECK_INTERVAL_MS);
      if (options.visibility.isVisible()) pollNow();
    },
    stop,
    pollNow,
    get running() {
      return running;
    },
  };
}
